import { supabase } from '@/integrations/supabase/client';

export interface LiveGameSession {
  id: string;
  game_id: string;
  scorekeeper_id: string;
  session_status: string;
  current_period?: number;
  time_remaining?: string;
  home_team_score: number;
  away_team_score: number;
  home_timeouts_left?: number;
  away_timeouts_left?: number;
  session_data?: any;
  started_at: string;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PlayerGameStat {
  id: string;
  game_session_id: string;
  player_id: string;
  period_quarter: string;
  stats_data: Record<string, number>;
  recorded_by: string;
  recorded_at: string;
}

export interface SportConfig {
  id: string;
  sport_id: number;
  config_name: string;
  scoring_fields: {
    periods: string[];
    score_types?: string[];
    game_state?: string[];
  };
  stat_fields: {
    stats: Array<{
      name: string;
      display: string;
      type: string;
    }>;
  };
}

export const liveGameService = {
  // Create a new live game session
  async createSession(gameId: string, scorekeeperId: string): Promise<LiveGameSession | null> {
    try {
      const { data, error } = await supabase
        .from('live_game_sessions')
        .insert({
          game_id: gameId,
          scorekeeper_id: scorekeeperId || '00000000-0000-0000-0000-000000000000', // Use fallback UUID
          session_status: 'active',
          home_team_score: 0,
          away_team_score: 0,
          current_period: 1,
          home_timeouts_left: 7,
          away_timeouts_left: 7,
          session_data: {
            events: []
          }
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating session:', error);
        return null;
      }

      return data as unknown as LiveGameSession;
    } catch (error) {
      console.error('Error creating live game session:', error);
      return null;
    }
  },

  // Get active session for a game
  async getActiveSession(gameId: string): Promise<LiveGameSession | null> {
    try {
      const { data, error } = await supabase
        .from('live_game_sessions')
        .select('*')
        .eq('game_id', gameId)
        .eq('session_status', 'active')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error getting session:', error);
        return null;
      }

      return (data as unknown as LiveGameSession) || null;
    } catch (error) {
      console.error('Error getting active session:', error);
      return null;
    }
  },

  // Update game scores
  async updateGameScore(sessionId: string, homeScore: number, awayScore: number): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('live_game_sessions')
        .update({
          home_team_score: homeScore,
          away_team_score: awayScore,
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (error) {
        console.error('Error updating scores:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating game score:', error);
      return false;
    }
  },

  // Record player statistics using existing player_stats table
  async recordPlayerStat(
    gameId: string,
    playerId: string,
    sportId: number,
    statType: string,
    value: number,
    recordedBy: string
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('player_stats')
        .insert({
          player_id: playerId,
          game_id: parseInt(gameId),
          sport_id: sportId,
          stat_type: statType,
          stat_value: value,
          stat_submitted_by: recordedBy,
          submitter_role: 'scorekeeper',
          season_year: new Date().getFullYear()
        });

      if (error) {
        console.error('Error recording stat:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error recording player stat:', error);
      return false;
    }
  },

  // Get sport configuration (hardcoded for now since table may not be in types)
  getSportConfig(sportId: number): SportConfig {
    const basketballConfig: SportConfig = {
      id: '1',
      sport_id: 1,
      config_name: 'Basketball',
      scoring_fields: {
        periods: ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter'],
        score_types: ['2-Point Field Goal', '3-Point Field Goal', 'Free Throw']
      },
      stat_fields: {
        stats: [
          { name: '2pt_attempts', display: '2-Point Attempts', type: 'number' },
          { name: '2pt_made', display: '2-Point Made', type: 'number' },
          { name: '3pt_attempts', display: '3-Point Attempts', type: 'number' },
          { name: '3pt_made', display: '3-Point Made', type: 'number' },
          { name: 'ft_attempts', display: 'Free Throw Attempts', type: 'number' },
          { name: 'ft_made', display: 'Free Throws Made', type: 'number' },
          { name: 'fouls', display: 'Fouls', type: 'number' },
          { name: 'turnovers', display: 'Turnovers', type: 'number' },
          { name: 'rebounds', display: 'Rebounds', type: 'number' },
          { name: 'assists', display: 'Assists', type: 'number' }
        ]
      }
    };

    return basketballConfig; // Default to basketball for now
  },

  // Complete scoring session
  async completeSession(sessionId: string): Promise<boolean> {
    try {
      // Get session details before completing for activity tracking
      const { data: session } = await supabase
        .from('live_game_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();
      
      const { error } = await supabase
        .from('live_game_sessions')
        .update({
          session_status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (error) {
        console.error('Error completing session:', error);
        return false;
      }

      // Track game score activity (fire-and-forget)
      if (session && session.scorekeeper_id) {
        this.trackGameCompleteActivity(session).catch(err =>
          console.error('Failed to track game score activity:', err)
        );
      }

      return true;
    } catch (error) {
      console.error('Error completing session:', error);
      return false;
    }
  },

  // Helper to track game completion activity
  async trackGameCompleteActivity(session: any): Promise<void> {
    try {
      const { activityTrackingService } = await import('./activityTrackingService');
      
      // Get game details for team names
      const { data: game } = await supabase
        .from('games')
        .select('id, home_team:teams!games_home_team_id_fkey(team_name), away_team:teams!games_away_team_id_fkey(team_name)')
        .eq('id', session.game_id)
        .single();
      
      const homeTeam = (game?.home_team as any)?.team_name || 'Home Team';
      const awayTeam = (game?.away_team as any)?.team_name || 'Away Team';
      
      await activityTrackingService.recordGameScore(
        session.scorekeeper_id,
        session.game_id,
        {
          homeTeam,
          awayTeam,
          homeScore: session.home_team_score || 0,
          awayScore: session.away_team_score || 0
        }
      );
    } catch (err) {
      console.error('Error tracking game complete:', err);
    }
  }
};